// Order create — 2-column layout (Operate & Logistics standard).
// Title: "Tạo đơn hàng".
// Hidden sales channels (default Manual).
// Dimensions configured via Products tab, inherited automatically.
// Live Rate Card integration with instant tier pricing.

import React, { useState, useMemo, useEffect, useRef, lazy, Suspense } from "react"
import { SenderAddressCard } from "@/components/orders/SenderAddressCard"
import { MobileActionBar } from "@/components/ui/mobile-action-bar"
import { useIsMobile } from "@/hooks/useIsMobile"
import { useCustomerSuggest } from "@/components/customers/CustomerSuggest"
import { CustomerContactBook } from "@/components/customers/CustomerContactBook"
import { AddressMiniMap } from "@/components/orders/AddressMiniMap"
import { ProductPickerDialog, type ProductOpt } from "@/components/orders/ProductPickerDialog"
import { QuickProductDialog } from "@/components/orders/QuickProductDialog"
import { useUnitCan } from "@/lib/unitAccess"
import { SaveCustomerOnLeaveDialog } from "@/components/orders/SaveCustomerOnLeaveDialog"
import { useLeaveGuard } from "@/hooks/useLeaveGuard"
import { customerToSave, type CustomerToSave } from "@/lib/orderLeave"
import type { SavedAddress, SuggestCustomer } from "@/components/customers/types"
import { addressLine, pickDefaultAddress } from "@shoptrack/common/customer"
import { useNavigate, useSearchParams, Link } from "react-router-dom"
import { useForm, useWatch, Controller, useFieldArray } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { toast } from "sonner"
import {
  ArrowLeft,
  Loader2,
  Sparkles,
  MapPin,
  Package,
  Truck,
  User,
  CreditCard,
  CheckCircle2,
  RotateCcw,
  Tag,
  Info,
  Layers,
  AlertTriangle,
  Building2,
  ExternalLink,
  Trash2,
  ShoppingBag,
  RefreshCw,
  X,
  Plus,
  Search,
  Pencil,
  FileSpreadsheet,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { MoneyInput } from "@/components/ui/money-input"
import { PhoneInput } from "@/components/ui/phone-input"
import { AddressInput } from "@/components/address/AddressInput"
import type { AreaIssue } from "@shoptrack/common/admin-areas"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { makeClientOrderId, money, isValidVnPhone, formatDate, normalizePhoneVN, parseDecimal, sanitizeDecimalInput } from "@/lib/format"
import { SEND_AT_MIN_LEAD_MS, defaultSendAt, fromLocalInput, sendAtError, sendAtLabel, toLocalInput } from "@/lib/sendAt"
import { get, post } from "@/lib/api"
import { useProfileIncompleteHandler } from "@/lib/onboarding"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { cn } from "@/lib/utils"
import { MAX_PARCEL_GRAM, MIN_PARCEL_GRAM, resolveParcel } from "@shoptrack/common/price-grid"
import { parseVietnameseAddress } from "@/lib/vietnamAddress"
import {
  fetchProvincesApi,
  fetchDistrictsApi,
  fetchWardsApi,
  findProvinceMatch,
  findDistrictMatch,
  findWardMatch,
  matchAdminDivisions,
} from "@/lib/provincesApi"
import { AddressMapPickerModal, type MapPickedAddress } from "@/components/AddressMapPickerModal"
import { resolveAdministrativeAddress } from "@/lib/vietnamAddressResolver"
import { celebrateSeason } from "@/lib/season/store"
import {
  resolveShippingRoute,
  ROUTE_BADGES,
  type ShippingRoute,
} from "@/lib/routeResolver"
import { useLocation } from "react-router-dom"
import { useAdminAreas } from "@/hooks/useAdminAreas"
import { MessageSquareText } from "lucide-react"
import {
  gomdonOrderChannel,
  pickProduct,
  prefillAddress,
  prefillHints,
  readGomdonPrefill,
  type FindNewArea,
  type GomdonPrefill,
  type PrefillHints,
} from "@/features/gomdon/order-prefill"
import { PricingBlocked, useOrderBlock } from '@/components/pricing/PricingNotice'

const itemSchema = z.object({
  productId: z.string().nullable().optional(),
  name: z.string().min(1, "Vui lòng nhập tên sản phẩm"),
  sku: z.string().optional().nullable(),
  price: z.number().int().nonnegative().default(0),
  cost: z.number().int().nonnegative().default(0),
  qty: z.number().int().positive("Số lượng tối thiểu là 1").default(1),
  weightGram: z.number().int().nonnegative().default(0),
})

const schema = z
  .object({
    clientOrderId: z.string().min(4, "Mã đơn tối thiểu 4 ký tự").max(64),
    channel: z.enum(["POS", "FB Live", "Zalo", "TikTok", "Shopee", "Manual", "Other"]).default("Manual"),
    carrierId: z.string().default("best"),
    productId: z.string().nullable().optional(),
    customer: z.object({
      name: z.string().min(1, "Vui lòng nhập tên khách hàng"),
      phone: z.string().optional(),
      address: z.string().min(3, "Vui lòng nhập địa chỉ giao hàng (tối thiểu 3 ký tự)"),
    }),
    cod: z.number().int().nonnegative(),
    cost: z.number().int().nonnegative().default(0),
    items: z.array(itemSchema).default([]),
    note: z.string().optional(),
    createShipment: z.boolean().default(true),
    shipment: z
      .object({
        productName: z.string().default("Hàng hóa"),
        weightGram: z.number().int().positive().default(500),
        destCity: z.string().default(""),
        destDistrict: z.string().optional(),
        destWard: z.string().default(""),
        pickupType: z.union([z.literal(1), z.literal(2)]).default(2),
        shipmentPayer: z.union([z.literal(0), z.literal(1)]).default(0),
        warehouseId: z.number().int().positive().optional(),
        productPrice: z.number().nonnegative().optional(),
        length: z.number().nonnegative().optional(),
        width: z.number().nonnegative().optional(),
        height: z.number().nonnegative().optional(),
        note: z.string().optional(),
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (data.createShipment) {
      if (!data.customer.phone || !data.customer.phone.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["customer", "phone"],
          message: "Bắt buộc nhập số điện thoại người nhận khi tạo vận đơn",
        })
      } else if (!isValidVnPhone(data.customer.phone)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["customer", "phone"],
          message: "Số điện thoại không đúng định dạng (cần 10 số di động VN: 03, 05, 07, 08, 09)",
        })
      }
      if (!data.shipment?.destCity?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["shipment", "destCity"],
          message: "Vui lòng chọn Tỉnh / Thành phố nhận hàng",
        })
      }
      if (!data.shipment?.destWard?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["shipment", "destWard"],
          message: "Vui lòng chọn Phường / Xã nhận hàng",
        })
      }
    } else {
      if (data.customer.phone && !isValidVnPhone(data.customer.phone)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["customer", "phone"],
          message: "Số điện thoại không đúng định dạng (cần 10 số di động VN: 03, 05, 07, 08, 09)",
        })
      }
    }
  })
type FormValues = z.infer<typeof schema>

interface ShippingQuote {
  zone: string
  zoneLabel: string
  chargeableWeightGram: number
  actualWeightGram?: number
  volumetricWeightGram?: number
  sellerPrice: number
  passThroughEstimate: number
  returnFeePercent: number
  returnFeeEstimate: number
  /** Khác null → đơn với cân này hiện chưa tạo được vận đơn (server sẽ từ chối). */
  shipmentBlocked?: { code: string; message: string } | null
}

interface WarehouseOpt {
  id: number
  name: string
  contactName: string
  contactPhone: string
  address: string
  city: string
  district: string | null
  ward: string
  isDefault: boolean
}


const QUICK_NOTES = [
  "Cho xem hàng không cho thử",
  "Cho thử hàng",
  "Không cho xem hàng",
  "Gọi khách trước khi giao",
  "Hàng dễ vỡ xin nhẹ tay",
  "Giao giờ hành chính",
]

/**
 * Tắt tự điền của trình duyệt / trình quản lý mật khẩu ở các ô người nhận: Safari tự chèn icon danh bạ
 * vào ô tên / SĐT / địa chỉ (bấm vào không làm gì trong form này). Kèm tên ô có chữ "search" — Safari
 * bỏ qua những ô đó.
 */
const NO_AUTOFILL = { autoComplete: "off", "data-1p-ignore": true, "data-lpignore": "true", "data-form-type": "other" } as const

// Hộp nhập file kéo theo thư viện đọc Excel — chỉ tải khi bấm "Tạo đơn bằng Excel"
const ImportOrdersModal = lazy(() => import("@/components/ImportOrdersModal").then((m) => ({ default: m.ImportOrdersModal })))

export default function OrderCreatePage() {
  const nav = useNavigate()
  // Chưa có bảng giá (mình hoặc cấp trên) → trang báo bị chặn thay cho form (docs/KE_HOACH_BANG_GIA_KHI_TAO_DON_VI.md §4.5)
  const priceBlock = useOrderBlock()
  // Tạo đơn bằng Excel (30/10/2026 chuyển từ thanh đầu vào đây); nhập xong sang danh sách đơn
  const [importOpen, setImportOpen] = useState(false)
  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 mb-6">
        <div className="flex items-center gap-3">
          <Link
            to="/orders"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 min-h-11 desktop:min-h-0 rounded-lg border border-rule bg-raise hover:bg-paperSoft text-xs font-medium text-inkSoft hover:text-ink transition-colors"
          >
            <span>Quay lại</span>
          </Link>
          <div>
            <h1>
              Tạo đơn
            </h1>
          </div>
        </div>
        {!priceBlock && (
          <Button type="button" variant="outline" onClick={() => setImportOpen(true)} className="mobile:w-full">
            <FileSpreadsheet className="size-4" aria-hidden />
            Tạo đơn bằng Excel
          </Button>
        )}
      </div>

      {priceBlock ? <PricingBlocked text={priceBlock} /> : <OrderCreateForm onSuccess={(orderId) => nav(`/orders/${orderId}`)} />}
      {importOpen && (
        <Suspense fallback={null}>
          <ImportOrdersModal open onClose={() => setImportOpen(false)} onSuccess={() => nav("/orders")} />
        </Suspense>
      )}
    </div>
  )
}

/**
 * Modal wrapper hỗ trợ mở OrderCreate dạng pop-up khi cần ("cả modal")
 */
export function OrderCreateModal({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean
  onClose: () => void
  onSuccess?: (orderId: string) => void
}) {
  const priceBlock = useOrderBlock()
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader className="mb-4 pb-3 border-b border-rule">
          <DialogTitle className="text-lg font-bold">Tạo đơn</DialogTitle>
        </DialogHeader>
        {priceBlock ? (
          <PricingBlocked text={priceBlock} className="shadow-none" />
        ) : (
          <OrderCreateForm
            onSuccess={(id) => {
              onClose()
              onSuccess?.(id)
            }}
            onCancel={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

/**
 * Main form component responsive (mobile + desktop)
 */
export function OrderCreateForm({
  onSuccess,
  onCancel,
}: {
  onSuccess?: (orderId: string) => void
  onCancel?: () => void
}) {
  const [submitting, setSubmitting] = useState(false)
  const qc = useQueryClient()
  const nav = useNavigate()
  const handleProfileIncomplete = useProfileIncompleteHandler()
  const [customerMapOpen, setCustomerMapOpen] = useState(false)
  const [orderLat, setOrderLat] = useState<number | null>(null)
  /** Khu vực giao chưa đủ chắc để gửi BEST — chặn tạo vận đơn (docs/KE_HOACH_DIA_CHI_CU_MOI.md) */
  const [areaIssue, setAreaIssue] = useState<AreaIssue | null>(null)
  const [orderLng, setOrderLng] = useState<number | null>(null)
  const [orderGeoAddress, setOrderGeoAddress] = useState<string | null>(null)
  // Khách quen đã chọn (gợi ý dưới ô tên / SĐT, hoặc ?customerId= từ trang Khách hàng) + địa chỉ đã lưu đang dùng
  const [picked, setPicked] = useState<SuggestCustomer | null>(null)
  const [pickedAddressId, setPickedAddressId] = useState<string | null>(null)
  // SĐT đã bấm "Bỏ qua" ở gợi ý khách quen — không hỏi lại cho đúng số đó
  const [dismissedPhone, setDismissedPhone] = useState<string | null>(null)
  // "Khách quen" = chọn từ danh bạ; "Khách lạ" = nhập tay (mặc định)
  const [customerMode, setCustomerMode] = useState<"regular" | "new">("new")
  const [pickingId, setPickingId] = useState<string | null>(null)
  const [searchParams] = useSearchParams()
  const isMobile = useIsMobile()


  // Fetch products
  const { data: productData } = useQuery({
    queryKey: ["products-active"],
    queryFn: () => get<{ products: ProductOpt[] }>("/products?active=true"),
    // Tồn kho đổi cả ở nơi khác (admin từ chối duyệt, hủy đơn, BEST hoàn) — mở lại là tải mới
    staleTime: 0,
  })
  // Bảng "Chọn sản phẩm" / form "+" thêm sản phẩm mới vào kho
  const [pickerOpen, setPickerOpen] = useState(false)
  const [quickProduct, setQuickProduct] = useState<{ name: string } | null>(null)
  // Nhân viên (docs/KE_HOACH_THANH_VIEN_DON_VI.md): phần theo quyền — chủ đơn vị: mọi thứ như trước
  const { can, seesMoney } = useUnitCan()
  const canManageProducts = can("products.manage")
  const canShipPrepay = can("orders.ship_prepay")
  const seesRates = can("rates.view")
  // Sửa sản phẩm đang có trong đơn ngay tại form (giữ sản phẩm lúc hộp đang đóng dần)
  const [editing, setEditing] = useState<{ product: ProductOpt; open: boolean } | null>(null)
  // Sản phẩm chọn được qua tìm trên máy chủ (ngoài 200 dòng đã tải) hoặc vừa thêm mới — vẫn biết tồn kho / ảnh
  const [extraProducts, setExtraProducts] = useState<Record<string, ProductOpt>>({})
  const findProduct = (id: string | null | undefined): ProductOpt | undefined =>
    id ? (productData?.products?.find((x) => x.id === id) ?? extraProducts[id]) : undefined


  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      clientOrderId: makeClientOrderId("DH"),
      channel: "Manual",
      carrierId: "best",
      productId: null,
      customer: { name: "", address: "" },
      cod: 0,
      cost: 0,
      items: [],
      createShipment: true,
      shipment: {
        productName: "Hàng hóa",
        weightGram: 500,
        destCity: "",
        destDistrict: "",
        destWard: "",
        pickupType: 2,
        // Không có quyền chuyển tiền ship QR → mặc định khách trả (shop trả cần chuyển khoản trước)
        shipmentPayer: canShipPrepay ? 0 : 1,
        productPrice: 0,
        note: "",
      },
    },
  })

  const { fields: itemFields, append: appendItem, remove: removeItem, update: updateItem } = useFieldArray({
    control: form.control,
    name: "items",
  })

  const watchItems = useWatch({ control: form.control, name: "items" }) || []
  const watchCod = useWatch({ control: form.control, name: "cod" }) || 0
  const watchPayer = useWatch({ control: form.control, name: "shipment.shipmentPayer" }) ?? 0
  const watchCustomerAddress = useWatch({ control: form.control, name: "customer.address" })
  const watchDestCity = useWatch({ control: form.control, name: "shipment.destCity" })
  const watchDestDistrict = useWatch({ control: form.control, name: "shipment.destDistrict" })
  const watchDestWard = useWatch({ control: form.control, name: "shipment.destWard" })
  const watchCreateShipment = useWatch({ control: form.control, name: "createShipment" })
  const watchPickupType = useWatch({ control: form.control, name: "shipment.pickupType" }) ?? 2

  // Realtime items aggregations
  const totalItemsQty = useMemo(() => {
    return watchItems.reduce((acc, it) => acc + (Number(it?.qty) || 0), 0)
  }, [watchItems])

  const totalItemsSubtotal = useMemo(() => {
    return watchItems.reduce((acc, it) => acc + ((Number(it?.qty) || 0) * (Number(it?.price) || 0)), 0)
  }, [watchItems])

  const totalItemsCost = useMemo(() => {
    return watchItems.reduce((acc, it) => acc + ((Number(it?.qty) || 0) * (Number(it?.cost) || 0)), 0)
  }, [watchItems])

  const totalItemsWeight = useMemo(() => {
    return watchItems.reduce((acc, it) => acc + ((Number(it?.qty) || 0) * (Number(it?.weightGram) || 0)), 0)
  }, [watchItems])

  // Cân quy đổi (D×R×C / 6000) theo kích thước cài ở trang Sản phẩm — cùng công thức với server
  const totalItemsVolumetric = useMemo(() => {
    return watchItems.reduce((acc, it) => {
      const p = findProduct(it?.productId)
      if (!p?.lengthCm || !p.widthCm || !p.heightCm) return acc
      return acc + (Number(it.qty) || 0) * Math.ceil((p.lengthCm * 10 * p.widthCm * 10 * p.heightCm * 10) / 6000)
    }, 0)
  }, [watchItems, productData, extraProducts]) // eslint-disable-line react-hooks/exhaustive-deps -- findProduct đọc đúng hai nguồn này

  // Kiện hàng người bán khai (nhiều món đóng chung một thùng). Cân chạy theo tổng cân sản phẩm cho tới
  // khi người bán sửa; xoá trống / sửa lại đúng tổng thì chạy theo lại. Kích thước trống = theo sản phẩm.
  const [parcelWeightText, setParcelWeightText] = useState<string | null>(null)
  const [parcelDimText, setParcelDimText] = useState<[string, string, string]>(["", "", ""])
  const autoParcelGram = Math.max(MIN_PARCEL_GRAM, Math.round(totalItemsWeight))
  const declaredParcelGram = parcelWeightText != null ? parseDecimal(parcelWeightText) : null
  const declaredDimsMm = parcelDimText.map((t) => {
    const cm = parseDecimal(t)
    return cm ? Math.round(cm * 10) : null
  })
  const parcel = resolveParcel(
    { actualGram: autoParcelGram, volumetricGram: totalItemsVolumetric, dims: null },
    {
      weightGram: declaredParcelGram ? Math.round(declaredParcelGram * 1000) : null,
      lengthMm: declaredDimsMm[0],
      widthMm: declaredDimsMm[1],
      heightMm: declaredDimsMm[2],
    },
  )
  const parcelTooHeavy = parcel.weightGram > MAX_PARCEL_GRAM
  const parcelDimsPartial = declaredDimsMm.some(Boolean) && !declaredDimsMm.every(Boolean)
  // Chỉ gửi máy chủ số người bán khai (số nguyên — deps của báo giá phải ổn định giữa các lần render)
  const quoteWeightGram = declaredParcelGram ? parcel.weightGram : undefined
  const [quoteL, quoteW, quoteH] = declaredDimsMm.every(Boolean) && parcel.dims
    ? [parcel.dims.lengthMm, parcel.dims.widthMm, parcel.dims.heightMm]
    : [null, null, null]
  const kgText = (g: number) => (g / 1000).toLocaleString("vi-VN", { maximumFractionDigits: 3 })
  // Ô số hiện dấu phẩy thập phân như cách viết tiếng Việt (giá trị giữ dấu chấm)
  const commaDecimal = (t: string) => t.replace(".", ",")

  // Check Shop's bank accounts (mandatory for COD reconciliation)
  const { data: bankData, isLoading: loadingBanks } = useQuery({
    queryKey: ["banks"],
    queryFn: () => get<{ banks: any[] }>("/banks"),
    staleTime: 1000 * 60 * 5,
  })
  const bankAccounts = bankData?.banks ?? []
  const hasBankAccount = bankAccounts.length > 0

  // Kho lấy hàng — BEST cần địa chỉ lấy hàng + hoàn cho mọi vận đơn. Bỏ trống = kho mặc định.
  const { data: warehouseData, isLoading: loadingWarehouses } = useQuery({
    queryKey: ["warehouses"],
    queryFn: () => get<{ warehouses: WarehouseOpt[] }>("/settings/warehouses"),
    staleTime: 1000 * 60 * 5,
  })
  const warehouses = warehouseData?.warehouses ?? []
  const watchWarehouseId = useWatch({ control: form.control, name: "shipment.warehouseId" })
  const selectedWarehouse = warehouses.find((w) => w.id === watchWarehouseId) ?? warehouses.find((w) => w.isDefault) ?? warehouses[0]
  const hasWarehouseAddress = Boolean(selectedWarehouse)


  // Tự động phân giải vùng giao hàng (Nội tỉnh / Liên tỉnh / Nội miền / Liên miền)
  const currentRoute = useMemo(() => {
    const senderCity = selectedWarehouse?.city || ''
    const destCity = watchDestCity || watchCustomerAddress || ''
    return resolveShippingRoute(senderCity, destCity)
  }, [selectedWarehouse?.city, watchDestCity, watchCustomerAddress])

  // Báo giá từ server theo bảng giá của CHÍNH người bán (GĐ5) — server tính vùng theo
  // kho đã chọn, cân tính cước = max(cân thực, quy đổi), phí BEST ngoài khung ước tính (D29).
  const watchDeclared = useWatch({ control: form.control, name: "shipment.productPrice" })
  const quoteInput = useMemo(
    () => ({
      destCity: watchDestCity || "",
      destDistrict: watchDestDistrict || null,
      destWard: watchDestWard || null,
      // Server tự tính cân thực / quy đổi / kích thước từ sản phẩm (giống lúc tạo đơn); kiện khai thì theo số khai
      items: watchItems.filter((it) => it?.productId).map((it) => ({ productId: String(it.productId), qty: Math.max(1, Number(it.qty) || 1) })),
      weightGram: quoteWeightGram,
      length: quoteL,
      width: quoteW,
      height: quoteH,
      warehouseId: selectedWarehouse?.id ?? null,
      cod: watchCod,
      declaredValue: watchDeclared ? Math.round(Number(watchDeclared)) : 0,
      withBestEstimate: true,
    }),
    [watchDestCity, watchDestDistrict, watchDestWard, watchItems, selectedWarehouse?.id, watchCod, watchDeclared, quoteWeightGram, quoteL, quoteW, quoteH],
  )
  const [debouncedQuote, setDebouncedQuote] = useState(quoteInput)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuote(quoteInput), 400)
    return () => clearTimeout(t)
  }, [quoteInput])
  const { data: quote, error: quoteError, isFetching: quoting } = useQuery({
    queryKey: ["shipping-quote", debouncedQuote],
    queryFn: () => post<ShippingQuote>("/shipping/quote", debouncedQuote),
    enabled: Boolean(watchCreateShipment && debouncedQuote.destCity && hasWarehouseAddress && debouncedQuote.items.length > 0),
    retry: false,
    staleTime: 60_000,
  })
  const sellerPrice = watchCreateShipment ? quote?.sellerPrice ?? 0 : 0
  // Chỉ chặn khi có tạo vận đơn — đơn không vận đơn vẫn tạo bình thường.
  const shipmentBlocked = watchCreateShipment ? quote?.shipmentBlocked ?? null : null

  // Phí ship thu khách (§2.6.2): "Shop trả" = 0 (ô ẩn, không cộng vào COD); "Khách trả" điền
  // sẵn giá của bạn, sửa được. Không tạo vận đơn → không có phí ship thu khách.
  const [customerShippingFee, setCustomerShippingFee] = useState(0)
  const [feeTouched, setFeeTouched] = useState(false)
  useEffect(() => {
    if (watchPayer === 1 && !feeTouched) setCustomerShippingFee(quote?.sellerPrice ?? 0)
  }, [watchPayer, feeTouched, quote?.sellerPrice])
  const customerPaysShipping = Boolean(watchCreateShipment) && watchPayer === 1
  /** Phí ship thực sự thu của khách — 0 khi Shop trả hoặc không tạo vận đơn. */
  const feeFor = (createShipment: boolean | undefined, payer: number | undefined) =>
    createShipment && payer === 1 ? Math.max(0, customerShippingFee) : 0

  // COD = tiền hàng + phí ship thu khách — đúng số bưu tá thu, không cộng ngầm.
  const goodsValue = watchCod
  const shipFee = feeFor(watchCreateShipment, watchPayer)
  const codTotal = goodsValue + shipFee
  // Shop trả ship (docs/KE_HOACH_SHOP_TRA_SHIP_QR.md): tiền hàng tự do; shop chuyển khoản trước
  // tiền ship theo bảng giá qua QR, ShopTrack duyệt rồi mới gửi BEST. Phí BEST ngoài bảng giá do NPP chịu
  // (BEST_EXTRAS_PAYER, 22/10/2026) — shop chỉ trả đúng bảng giá.
  const shopPaysShipping = Boolean(watchCreateShipment) && watchPayer === 0
  const prepayEstimate = shopPaysShipping ? sellerPrice : 0
  // Thực nhận dự kiến = COD − cước theo bảng giá của bạn. Được phép âm.
  // Shop trả: cước đã chuyển khoản riêng → khi giao nhận đủ COD.
  const netEstimate = shopPaysShipping ? codTotal : codTotal - sellerPrice

  // Address lookup APIs
  const { data: provinceList = [] } = useQuery({
    queryKey: ["provinces-api"],
    queryFn: fetchProvincesApi,
    staleTime: 1000 * 60 * 60 * 24,
  })

  // Quick Address Parser
  async function handleAutoDetect(textToParse?: string) {
    const raw = (textToParse ?? form.getValues("customer.address"))?.trim()
    if (!raw) {
      toast.error("Vui lòng dán hoặc nhập địa chỉ để phân tích")
      return
    }
    const p = parseVietnameseAddress(raw)
    let foundCity = p.city
    let foundDistrict = p.district
    let foundWard = p.ward

    const matchedProv = findProvinceMatch(provinceList, foundCity || raw)
    if (matchedProv) {
      foundCity = matchedProv.cleanName
      try {
        const dists = await fetchDistrictsApi(matchedProv.code)
        const matchedDist = findDistrictMatch(dists, foundDistrict || raw)
        if (matchedDist) {
          foundDistrict = matchedDist.name
          const wrds = await fetchWardsApi(matchedDist.code)
          const matchedWrd = findWardMatch(wrds, foundWard || raw)
          if (matchedWrd) foundWard = matchedWrd.name
        }
      } catch {
        // ignore
      }
    }

    if (foundCity) form.setValue("shipment.destCity", foundCity, { shouldValidate: true })
    if (foundDistrict) form.setValue("shipment.destDistrict", foundDistrict, { shouldValidate: true })
    if (foundWard) form.setValue("shipment.destWard", foundWard, { shouldValidate: true })
    if (p.address) form.setValue("customer.address", p.address, { shouldValidate: true })

    const found = [foundWard, foundDistrict, foundCity].filter(Boolean).join(", ")
    if (found) {
      toast.success(`Đã nhận diện: ${found}`)
    } else {
      toast.info("Vui lòng chọn Tỉnh/Thành bên dưới")
    }
  }

  // Address Map Picker callback
  const handleCustomerMapConfirm = async (picked: MapPickedAddress) => {
    const resolved = resolveAdministrativeAddress({
      city: picked.city,
      district: picked.district,
      ward: picked.ward,
      street: picked.address,
      fullAddress: picked.formattedAddress,
    })

    // Biết phường CŨ của điểm ghim → điền địa chỉ cũ (BEST giao đúng phường). Không thì địa chỉ 2 cấp (đơn vị
    // hành chính mới, vd. "Phường Sài Gòn" từ GPS): không có quận/huyện — vẫn điền phường/xã; quận/huyện cũ
    // (nếu có) được xóa để không lệch với phường mới.
    const { city, district, ward } = picked.old ?? await matchAdminDivisions(provinceList, {
      city: resolved.city || picked.city,
      district: resolved.district || picked.district,
      ward: resolved.ward || picked.ward,
      fullText: picked.formattedAddress,
    })
    const address = resolved.street || picked.address

    if (city) {
      form.setValue("shipment.destCity", city, { shouldValidate: true })
      form.setValue("shipment.destDistrict", district, { shouldValidate: true })
      form.setValue("shipment.destWard", ward, { shouldValidate: true })
    } else if (ward) {
      form.setValue("shipment.destWard", ward, { shouldValidate: true })
    }
    if (address) form.setValue("customer.address", address, { shouldValidate: true })

    setOrderLat(picked.lat)
    setOrderLng(picked.lng)
    setOrderGeoAddress(picked.formattedAddress)

    const summary = [address, ward, district, city].filter(Boolean).join(", ")
    toast.success(`Đã định vị thành công: ${summary}`)
  }

  // ─── Khách quen ───────────────────────────────────────────────────────────────────────────
  const watchCustomerName = useWatch({ control: form.control, name: "customer.name" }) ?? ""
  const watchCustomerPhone = useWatch({ control: form.control, name: "customer.phone" }) ?? ""
  const phoneDigits = String(watchCustomerPhone).replace(/\D/g, "")

  // Điền địa chỉ đã lưu: tên tỉnh / quận / phường chuẩn hoá theo danh mục hành chính (như ghim bản đồ);
  // địa chỉ cũ chưa tách tỉnh / phường → tự tách từ dòng địa chỉ.
  const applySeqRef = useRef(0)
  async function applyAddress(a: SavedAddress) {
    const seq = ++applySeqRef.current
    setPickedAddressId(a.id)
    setOrderLat(a.lat ?? null)
    setOrderLng(a.lng ?? null)
    setOrderGeoAddress(a.lat != null ? a.geoAddress : null)
    if (!a.city && !a.ward) {
      // Xoá tỉnh / quận / phường của địa chỉ trước — tách không ra thì người bán chọn lại, không giữ nhầm tỉnh cũ
      form.setValue("shipment.destCity", "")
      form.setValue("shipment.destDistrict", "")
      form.setValue("shipment.destWard", "")
      form.setValue("customer.address", a.street, { shouldValidate: true })
      await handleAutoDetect(a.street)
      return
    }
    const m = await matchAdminDivisions(provinceList, { city: a.city, district: a.district, ward: a.ward, fullText: [a.ward, a.district, a.city].filter(Boolean).join(", ") })
    if (seq !== applySeqRef.current) return // đã đổi sang địa chỉ khác trong lúc tra danh mục
    form.setValue("shipment.destCity", m.city, { shouldValidate: true })
    form.setValue("shipment.destDistrict", m.district, { shouldValidate: true })
    form.setValue("shipment.destWard", m.ward, { shouldValidate: true })
    form.setValue("customer.address", a.street, { shouldValidate: true })
  }

  function applyCustomer(c: SuggestCustomer) {
    setPicked(c)
    setCustomerMode("regular")
    setDismissedPhone(null)
    form.setValue("customer.name", c.name, { shouldValidate: true })
    // Dòng cũ có thể lưu +84… — điền dạng 0… như ô SĐT dùng
    if (c.phone) form.setValue("customer.phone", normalizePhoneVN(c.phone), { shouldValidate: true })
    const a = pickDefaultAddress(c.addresses)
    if (a) void applyAddress(a)
    else setPickedAddressId(null)
  }

  /** Chọn một khách trong danh bạ → tải sổ địa chỉ của khách rồi điền form */
  async function pickFromBook(id: string) {
    setPickingId(id)
    try {
      const { customer } = await get<{ customer: SuggestCustomer }>(`/customers/${encodeURIComponent(id)}`)
      applyCustomer(customer)
    } catch {
      toast.error("Không tải được thông tin khách")
    } finally {
      setPickingId(null)
    }
  }

  /** Đổi tab Khách quen / Khách lạ. Sang Khách lạ từ khách đã chọn → form trống để nhập người mới. */
  function switchCustomerMode(mode: "regular" | "new") {
    if (mode === customerMode) return
    if (mode === "new" && picked) {
      setPicked(null)
      form.setValue("customer.name", "")
      form.setValue("customer.phone", undefined)
      clearAddress()
    }
    setCustomerMode(mode)
  }

  /** "Nhập địa chỉ khác" — giữ khách, xoá địa chỉ để nhập mới (sẽ được lưu vào sổ khi tạo đơn) */
  function clearAddress() {
    setPickedAddressId(null)
    setOrderLat(null)
    setOrderLng(null)
    setOrderGeoAddress(null)
    form.setValue("customer.address", "")
    form.setValue("shipment.destCity", "")
    form.setValue("shipment.destDistrict", "")
    form.setValue("shipment.destWard", "")
  }

  // Đổi SĐT sang số khác → không còn là khách đã chọn (so dạng chuẩn hoá — dòng cũ lưu +84…)
  useEffect(() => {
    if (picked?.phone && phoneDigits && phoneDigits !== normalizePhoneVN(picked.phone)) {
      setPicked(null)
      setPickedAddressId(null)
    }
  }, [phoneDigits, picked])

  // Gõ đủ SĐT của một khách đã lưu mà chưa chọn: form trống thì tự điền, đang có tên / địa chỉ thì hỏi
  const exactQuery = !picked && isValidVnPhone(phoneDigits) && dismissedPhone !== phoneDigits ? phoneDigits : ""
  const { data: exactData } = useCustomerSuggest(exactQuery, { enabled: Boolean(exactQuery), limit: 1 })
  const exactMatch = exactQuery ? exactData?.customers.find((c) => normalizePhoneVN(c.phone) === exactQuery) ?? null : null
  useEffect(() => {
    if (!exactMatch) return
    if (!form.getValues("customer.name")?.trim() && !form.getValues("customer.address")?.trim()) {
      applyCustomer(exactMatch)
      toast.success(`Đã điền theo khách quen: ${exactMatch.name}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chỉ khi có khách khớp SĐT mới
  }, [exactMatch?.id])

  // ─── Rời form khi đã nhập khách mới → hỏi lưu vào Khách quen ───────────────────────────────
  // Khách đã chọn / SĐT đã có trong danh bạ thì khỏi hỏi — khách đó đã được lưu
  const canOfferSave = !submitting && !picked && !exactMatch && Boolean(watchCustomerName.trim()) && isValidVnPhone(phoneDigits)
  const [leaving, setLeaving] = useState<{ customer: CustomerToSave; go: () => void; open: boolean } | null>(null)
  /** Rời form (đi tới `go`) — đang có khách mới chưa lưu thì hỏi trước */
  function requestLeave(go: () => void) {
    const v = form.getValues()
    const customer = canOfferSave
      ? customerToSave({
          name: v.customer.name,
          phone: v.customer.phone,
          street: v.customer.address,
          ward: v.shipment?.destWard,
          district: v.shipment?.destDistrict,
          city: v.shipment?.destCity,
          lat: orderLat,
          lng: orderLng,
          geoAddress: orderGeoAddress,
        })
      : null
    if (customer) setLeaving({ customer, go, open: true })
    else go()
  }
  // Thanh tab, menu, "Quay lại", "Hủy và quay lại danh sách"… đều là liên kết
  useLeaveGuard(canOfferSave && !leaving?.open, (to) => requestLeave(() => nav(to)))

  // Mở từ trang Khách hàng ("Tạo đơn cho khách này"): /orders/new?customerId=…
  const prefillCustomerId = searchParams.get("customerId")
  const prefilledRef = useRef(false)
  useEffect(() => {
    if (!prefillCustomerId || prefilledRef.current || !provinceList.length) return
    prefilledRef.current = true
    get<{ customer: SuggestCustomer }>(`/customers/${encodeURIComponent(prefillCustomerId)}`)
      .then(({ customer }) => applyCustomer(customer))
      .catch(() => toast.error("Không tải được thông tin khách"))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- một lần, khi danh mục tỉnh đã tải
  }, [prefillCustomerId, provinceList.length])

  // Mở từ Gom Đơn ("Lên đơn" ở khung bóc đơn): history state gomdonPrefill — không có thì form chạy y như cũ.
  // Áp MỘT lần khi danh mục tỉnh + danh mục địa chỉ đã tải; không tự gửi đơn. State bị xoá khỏi history ngay khi bắt đầu
  // áp (trước các lượt tra sản phẩm) — tải lại không áp nữa, rời trang giữa chừng không bị kéo về form.
  const location = useLocation()
  const gomdonState = location.state as unknown
  const { data: gomdonAreas } = useAdminAreas()
  const gomdonAppliedRef = useRef(false)
  // Ghi chú bóc đơn + món chưa khớp: chỉ hiện cho nhân viên, không điền vào ô nào, không gửi kèm đơn
  const [gomdonHints, setGomdonHints] = useState<PrefillHints | null>(null)
  useEffect(() => {
    if (gomdonAppliedRef.current || !provinceList.length || !gomdonAreas) return
    const p = readGomdonPrefill(gomdonState)
    if (!p) return
    gomdonAppliedRef.current = true
    nav(location.pathname + location.search, { replace: true, state: null })
    void applyGomdonPrefill(p, gomdonAreas.findNewArea)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- một lần, khi có state và danh mục đã tải
  }, [gomdonState, provinceList.length, gomdonAreas])

  async function applyGomdonPrefill(p: GomdonPrefill, findNewArea: FindNewArea) {
    form.setValue("channel", gomdonOrderChannel(p.channelType))
    if (p.customerName) form.setValue("customer.name", p.customerName, { shouldValidate: true })
    const phone = normalizePhoneVN(p.phone)
    if (phone) form.setValue("customer.phone", phone, { shouldValidate: true })
    const a = prefillAddress(p.address, findNewArea)
    if (a.street) form.setValue("customer.address", a.street, { shouldValidate: true })
    if (a.matched) {
      form.setValue("shipment.destCity", a.city, { shouldValidate: true })
      form.setValue("shipment.destDistrict", "", { shouldValidate: true })
      form.setValue("shipment.destWard", a.ward, { shouldValidate: true })
    }
    // Sản phẩm: chỉ sản phẩm có trong kho; không khớp → ghi chú để nhân viên tự chọn
    const unmatched: { name: string; quantity: number }[] = []
    for (const it of p.items) {
      try {
        const { products } = await get<{ products: ProductOpt[] }>(`/products?q=${encodeURIComponent(it.name)}&active=true`)
        const prod = pickProduct(it.name, products)
        if (!prod) {
          unmatched.push(it)
          continue
        }
        const have = Number(form.getValues("items")?.find((x) => x.productId === prod.id)?.qty) || 0
        if (prod.stock <= have) toast.warning(`"${prod.name}" đã hết hàng trong kho`)
        else if (prod.stock < have + it.quantity) toast.info(`Kho chỉ còn ${prod.stock} "${prod.name}"`)
        setProductQty(prod, have + it.quantity)
      } catch {
        unmatched.push(it)
      }
    }
    setGomdonHints(prefillHints(p.note, unmatched))
    toast.success("Đã điền sẵn từ Gom Đơn — kiểm tra lại rồi bấm tạo đơn")
  }

  // Đặt số lượng một sản phẩm kho trong đơn (đơn chỉ gồm sản phẩm có trong kho): 0 = bỏ khỏi đơn,
  // không vượt tồn kho. Đọc getValues — bấm "+" liên tục không bị lệch theo lần render trước.
  const setProductQty = (p: ProductOpt, qty: number) => {
    if (!productData?.products?.some((x) => x.id === p.id)) setExtraProducts((m) => ({ ...m, [p.id]: p }))
    const items = form.getValues("items") ?? []
    const idx = items.findIndex((it) => it.productId === p.id)
    const next = Math.min(p.stock, Math.max(0, Math.floor(qty)))
    if (idx >= 0) {
      const cur = items[idx]
      if (!cur) return
      if (next <= 0) removeItem(idx)
      else updateItem(idx, { ...cur, qty: next })
    } else if (next > 0) {
      appendItem({ productId: p.id, name: p.name, sku: p.sku || "", price: p.price || 0, cost: p.cost || 0, qty: next, weightGram: p.weightGram || 0 })
    }
  }

  // Sửa sản phẩm trong form (lỡ nhập sai) — đã lưu vào kho; dòng trong đơn đổi theo. Giá bán chỉ đổi khi
  // giá trong kho đổi (giữ giá đã giảm tay), số lượng không vượt tồn kho mới.
  const applyProductEdit = (before: ProductOpt, p: ProductOpt) => {
    setExtraProducts((m) => (m[p.id] ? { ...m, [p.id]: p } : m))
    const items = form.getValues("items") ?? []
    items.forEach((it, idx) => {
      if (it.productId !== p.id) return
      const qty = Math.min(Number(it.qty) || 1, Math.max(1, p.stock))
      if (qty < (Number(it.qty) || 1)) toast.info(`Kho chỉ còn ${p.stock} "${p.name}" — đã giảm số lượng trong đơn`)
      updateItem(idx, {
        ...it,
        name: p.name,
        sku: p.sku || "",
        cost: p.cost || 0,
        weightGram: p.weightGram || 0,
        price: p.price !== before.price ? p.price || 0 : it.price,
        qty,
      })
    })
  }

  const inCart = useMemo(() => {
    const m: Record<string, number> = {}
    for (const it of watchItems) if (it?.productId) m[String(it.productId)] = (m[String(it.productId)] ?? 0) + (Number(it.qty) || 0)
    return m
  }, [watchItems])

  // Auto-sync items to shipment name, weight, cost and cod
  useEffect(() => {
    if (watchItems.length > 0) {
      const names = watchItems
        .filter((it) => it?.name?.trim())
        .map((it) => `${it.name.trim()} (x${it.qty || 1})`)
        .join(", ")
        .slice(0, 200)
      if (names) {
        const curName = form.getValues("shipment.productName")
        if (!curName || curName === "Hàng hóa" || curName.includes("(x")) {
          form.setValue("shipment.productName", names)
        }
      }
      if (totalItemsWeight > 0) {
        form.setValue("shipment.weightGram", Math.max(50, totalItemsWeight))
      }
      form.setValue("cost", totalItemsCost)
      form.setValue("productId", watchItems[0]?.productId || null)
    }
  }, [watchItems, totalItemsWeight, totalItemsCost, form])

  // "Tiền hàng" (phần hàng của COD) chạy theo tổng tiền hàng khi thêm / bớt sản phẩm, đổi số lượng,
  // đổi đơn giá — cho tới khi người bán tự sửa ô này. Sửa lại đúng bằng tổng tiền hàng, hoặc bấm
  // "Đặt lại theo tiền hàng", thì chạy theo lại.
  const [codTouched, setCodTouched] = useState(false)
  useEffect(() => {
    if (codTouched) return
    if (form.getValues("cod") !== totalItemsSubtotal) form.setValue("cod", totalItemsSubtotal, { shouldValidate: true })
  }, [codTouched, totalItemsSubtotal, form])
  const resetCodToSubtotal = () => {
    setCodTouched(false)
    form.setValue("cod", totalItemsSubtotal, { shouldValidate: true })
  }

  // Hẹn gửi (docs/KE_HOACH_HEN_GIO_GUI_DON.md): tới giờ mới gửi hãng / gửi duyệt tiền ship — chỉ đơn có vận đơn
  const [scheduleOn, setScheduleOn] = useState(false)
  const [sendAtLocal, setSendAtLocal] = useState("")
  const scheduled = Boolean(watchCreateShipment) && scheduleOn
  const sendAtProblem = scheduled && sendAtLocal ? sendAtError(fromLocalInput(sendAtLocal)) : null
  const submitLabel = scheduled ? "Hẹn gửi" : shopPaysShipping ? "Tạo đơn & mã QR" : "Tạo đơn"

  async function onSubmit(v: FormValues) {
    // 1. Enforce Bank Account requirement
    if (!hasBankAccount) {
      toast.error("Vui lòng cài đặt Tài khoản ngân hàng trước khi tạo đơn hàng!")
      return
    }

    // 2. Enforce Warehouse Address if createShipment
    if (v.createShipment && !hasWarehouseAddress) {
      toast.error("Chưa có địa chỉ gửi hàng")
      return
    }

    const sendAt = v.createShipment && scheduleOn ? fromLocalInput(sendAtLocal) : null
    if (v.createShipment && scheduleOn) {
      const problem = sendAtError(sendAt)
      if (problem) {
        toast.error(problem)
        return
      }
    }

    setSubmitting(true)
    try {
      if (v.createShipment) {
        if (!v.shipment?.destCity?.trim()) {
          toast.error("Vui lòng chọn Tỉnh / Thành phố nhận hàng")
          setSubmitting(false)
          return
        }
        if (!v.shipment?.destWard?.trim()) {
          toast.error("Vui lòng chọn Phường / Xã nhận hàng")
          setSubmitting(false)
          return
        }
        // Khu vực phải đủ chắc để BEST giao đúng — không nhận ra / chưa chọn phường cũ thì chưa tạo vận đơn
        if (areaIssue) {
          toast.error(
            areaIssue === "unrecognized"
              ? "Chưa nhận ra khu vực giao — chọn lại Khu vực"
              : "Chọn phường cũ đúng với nhà khách",
          )
          setSubmitting(false)
          return
        }
        if (parcelTooHeavy || parcelDimsPartial) {
          toast.error(parcelTooHeavy ? `Cân nặng kiện tối đa ${kgText(MAX_PARCEL_GRAM)} kg` : "Nhập đủ dài, rộng, cao của kiện")
          setSubmitting(false)
          return
        }
      }

      // Bản sao sâu một cấp — chỉnh payload không được đụng vào giá trị đang hiện trên form
      const payload: any = { ...v, customer: { ...v.customer }, shipment: v.shipment ? { ...v.shipment } : undefined }
      // Clean phone
      if (payload.customer?.phone) {
        const cleanPhone = String(payload.customer.phone).replace(/[\s.-]/g, "")
        if (cleanPhone) payload.customer.phone = cleanPhone
        else delete payload.customer.phone
      } else {
        delete payload.customer?.phone
      }

      // Hàng hoá chỉ từ kho — server lấy tên, cân, kích thước, giá vốn từ sản phẩm
      payload.items = (payload.items ?? [])
          .filter((it: any) => it?.productId)
          .map((it: any) => ({
            productId: it.productId || null,
            name: it.name.trim(),
            sku: it.sku?.trim() || null,
            price: Number(it.price) || 0,
            cost: Number(it.cost) || 0,
            qty: Math.max(1, Number(it.qty) || 1),
            weightGram: Number(it.weightGram) || 0,
          }))
      if (!payload.items.length) {
        toast.error("Chọn ít nhất một sản phẩm từ kho")
        return
      }
      payload.productId = payload.items[0]?.productId || null

      if (payload.shipment) {
        // Kiện gửi hãng: cân người bán khai (mặc định tổng cân sản phẩm) + kích thước khai; trống = theo sản phẩm
        payload.shipment.weightGram = parcel.weightGram
        payload.shipment.length = quoteL
        payload.shipment.width = quoteW
        payload.shipment.height = quoteH
        if (payload.shipment.note) payload.note = payload.shipment.note
      }

      if (!v.createShipment) {
        // Không tạo vận đơn: đơn lưu địa chỉ một dòng (customer.address) — ghép đủ phường / quận /
        // tỉnh vào đó để đơn không mất địa chỉ hành chính. Kèm bản tách phần để lưu vào sổ địa chỉ của khách.
        const full = [v.customer.address, v.shipment?.destWard, v.shipment?.destDistrict, v.shipment?.destCity]
          .map((x) => x?.trim())
          .filter(Boolean)
          .join(", ")
        if (full) payload.customer.address = full.slice(0, 500)
        payload.customer.street = v.customer.address?.trim() || null
        payload.customer.ward = v.shipment?.destWard?.trim() || null
        payload.customer.district = v.shipment?.destDistrict?.trim() || null
        payload.customer.city = v.shipment?.destCity?.trim() || null
        delete payload.shipment
      }
      // Khách quen đã chọn — máy chủ vẫn theo SĐT nếu SĐT thuộc khách khác
      if (picked) payload.customerId = picked.id
      delete payload.createShipment

      if (orderLat != null && orderLng != null) {
        payload.lat = orderLat
        payload.lng = orderLng
        if (orderGeoAddress) {
          payload.geoAddress = orderGeoAddress
        }
      }

      // COD gửi BEST = tiền hàng + phí ship thu khách; luôn ShipmentPayer = 0 (D20).
      // Tính lại từ giá trị vừa gửi (không dùng số của lần render trước).
      const submitFee = feeFor(v.createShipment, v.shipment?.shipmentPayer)
      payload.cod = Math.max(0, Math.round(Number(v.cod) || 0)) + submitFee
      payload.customerShippingFee = submitFee
      // Ai trả cước: "shop" → máy chủ tạo đơn chờ duyệt + QR chuyển khoản tiền ship
      if (v.createShipment) payload.shippingPayer = v.shipment?.shipmentPayer === 1 ? "customer" : "shop"
      if (payload.shipment) payload.shipment.shipmentPayer = 0
      if (sendAt) payload.sendAt = sendAt.toISOString()

      const r = await post<{ orderId: string; sagaId: string | null; idempotent?: boolean; prepayment?: { amount: number; transferContent: string } | null; scheduled?: { sendAt: string } | null }>("/orders", payload)
      if (r.idempotent) {
        // Gửi lại đúng đơn vừa tạo (bấm 2 lần, mạng chập chờn) — không tạo đơn mới
        toast.info("Đơn này đã được tạo trước đó — mở đơn đã có")
      } else if (r.scheduled) {
        toast.success(`Đã hẹn gửi lúc ${sendAtLabel(r.scheduled.sendAt)}`)
      } else if (r.prepayment) {
        toast.success(`Đã tạo đơn — chuyển khoản ${money(r.prepayment.amount)} tiền ship theo mã QR`, {
          duration: 8_000,
        })
      } else {
        toast.success(v.createShipment ? "Đã tạo đơn và gửi sang hãng" : "Đã tạo đơn")
      }
      // Giao diện theo mùa: bung hiệu ứng của dịp (lì xì, cá heo, xe tuần lộc…) khi có đơn mới
      if (!r.idempotent) celebrateSeason()
      // Tồn kho vừa bị trừ, danh sách đơn / tổng quan có đơn mới — không đợi hết staleTime
      for (const key of ["products", "products-active", "orders", "overview", "customers", "reports.revenue"]) {
        qc.invalidateQueries({ queryKey: [key] })
      }
      onSuccess?.(r.orderId)
    } catch (err) {
      const code = (err as { code?: string })?.code
      if (code === "order.duplicate_client_id") {
        // Mã tham chiếu đã dùng cho một đơn khác → báo ngay ở ô mã, cho đổi mã một chạm
        const existingId = ((err as { details?: { orderId?: string } })?.details)?.orderId
        const msg = "Mã tham chiếu này đã dùng cho một đơn khác"
        form.setError("clientOrderId", { type: "server", message: msg })
        toast.error("Trùng mã đơn hàng", {
          description: `${v.clientOrderId} đã có trong hệ thống${existingId ? ` (đơn ${existingId})` : ""}.`,
          duration: 12_000,
          action: {
            label: "Đổi mã",
            onClick: () => {
              form.setValue("clientOrderId", makeClientOrderId("DH"), { shouldValidate: true })
              form.clearErrors("clientOrderId")
            },
          },
          ...(existingId
            ? { cancel: { label: "Xem đơn cũ", onClick: () => nav(`/orders/${encodeURIComponent(existingId)}`) } }
            : {}),
        })
        return
      }
      // 409 profile.incomplete: hồ sơ đơn vị chưa đủ → báo kèm nút "Hoàn tất thông tin"
      if (!handleProfileIncomplete(err)) toast.error((err as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  function onInvalid(errors: any) {
    const firstErr =
      errors?.customer?.name?.message ||
      errors?.customer?.address?.message ||
      errors?.customer?.phone?.message ||
      errors?.shipment?.destCity?.message ||
      errors?.shipment?.destWard?.message ||
      errors?.clientOrderId?.message ||
      "Vui lòng điền đầy đủ các thông tin bắt buộc"
    toast.error(firstErr)
  }

  // Dùng chung cho nút ở thẻ Thanh toán (máy tính) và thanh dính đáy (điện thoại)
  const submitDisabled =
    submitting ||
    (!loadingBanks && !hasBankAccount) ||
    (watchCreateShipment && !loadingWarehouses && !hasWarehouseAddress) ||
    Boolean(shipmentBlocked)

  return (
    <>
    <form onSubmit={form.handleSubmit(onSubmit, onInvalid)}>
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 items-start">
        {/* Gom Đơn "Lên đơn": ghi chú bóc đơn + món chưa khớp — chỉ để nhân viên xem, không gửi kèm đơn */}
        {gomdonHints && (
          <div role="note" aria-label="Thông tin từ Gom Đơn" className="xl:col-span-12 flex items-start gap-2.5 rounded-lg border border-accent/30 bg-accent/5 px-3.5 py-2.5 text-xs text-ink">
            <MessageSquareText className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden />
            <div className="min-w-0 flex-1 space-y-1">
              <p className="font-semibold">Từ hội thoại Gom Đơn (chỉ để bạn xem, không gửi kèm đơn)</p>
              {gomdonHints.note && <p className="whitespace-pre-wrap break-words"><span className="text-inkSoft">Ghi chú: </span>{gomdonHints.note}</p>}
              {gomdonHints.unmatched.length > 0 && (
                <ul className="space-y-0.5 text-warn">
                  {gomdonHints.unmatched.map((line, i) => <li key={i}>{line}</li>)}
                </ul>
              )}
            </div>
            <button type="button" onClick={() => setGomdonHints(null)} className="shrink-0 rounded p-1 text-muted hover:text-ink" aria-label="Ẩn thông tin từ Gom Đơn">
              <X className="size-3.5" />
            </button>
          </div>
        )}
        {/* Banner cảnh báo chưa cài ngân hàng nhận COD (tối giản 1 dòng) */}
        {!loadingBanks && !hasBankAccount && (
          <div className="xl:col-span-12 px-3.5 py-2 rounded-lg border border-warn/30 bg-warn/10 text-warn flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <span>Chưa cài đặt tài khoản ngân hàng</span>
            </div>
            <Link
              to="/settings?tab=banks"
              className="inline-flex items-center gap-1 font-semibold text-accent hover:underline shrink-0 text-xs ml-2"
            >
              <span>Ngân hàng</span>
            </Link>
          </div>
        )}

        {/* ─── CỘT TRÁI: NHẬP LIỆU (8 cols) ─── */}
        <div className="xl:col-span-8 space-y-3">
          {/* Hàng đầu: Gửi từ + Hàng hóa đứng cạnh nhau (máy tính) — tiết kiệm chiều cao trang */}
          <div className={cn("grid grid-cols-1 items-stretch gap-3", watchCreateShipment && "lg:grid-cols-2")}>
            {/* Gửi từ — địa chỉ gửi hàng (kho lấy hàng) của đơn này; đổi / thêm ngay tại chỗ */}
            {watchCreateShipment && (
              <SenderAddressCard
                addresses={warehouses}
                selected={selectedWarehouse}
                loading={loadingWarehouses}
                onSelect={(id) => form.setValue("shipment.warehouseId", id, { shouldDirty: true })}
                className="h-full"
                canAdd={can("warehouses.manage")}
              >
                      <Field label="Hình thức lấy hàng" className="mt-auto">
                        <Controller
                          control={form.control}
                          name="shipment.pickupType"
                          render={({ field }) => (
                            <Select value={String(field.value ?? 2)} onValueChange={(v) => field.onChange(Number(v))}>
                              <SelectTrigger className="bg-paper h-11 desktop:h-9"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="2">Bưu tá lấy tận nơi</SelectItem>
                                <SelectItem value="1">Shop mang ra bưu cục</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        />
                      </Field>
              </SenderAddressCard>
            )}

            {/* Sản phẩm — chỉ sản phẩm trong kho; tổng số lượng / cân / tiền hàng ngay ở tiêu đề */}
            <div className="h-full rounded-xl bg-raise p-3 sm:p-3.5 space-y-2 shadow-card">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <h2 className="font-semibold text-sm text-ink">Sản phẩm</h2>
                {itemFields.length > 0 && (
                  <span className="ml-auto text-xs text-inkSoft">
                    {totalItemsQty} SP · {(totalItemsWeight / 1000).toLocaleString("vi-VN", { maximumFractionDigits: 2 })} kg ·{" "}
                    <b className="font-mono text-accent">{money(totalItemsSubtotal)}</b>
                  </span>
                )}
              </div>

              {/* Chọn sản phẩm có sẵn → bảng riêng có tìm / lọc; "+" → thêm sản phẩm mới vào kho rồi cho vào đơn */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  className="flex h-11 desktop:h-9 min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-md border border-rule bg-paper px-3 text-sm text-inkSoft hover:border-accent/50 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30"
                >
                  <Search className="size-4 shrink-0 text-muted" />
                  <span className="truncate">Chọn sản phẩm có sẵn</span>
                  {productData && (
                    <span className="ml-auto shrink-0 text-xs tabular-nums text-muted">
                      {productData.products.length >= 200 ? "200+" : productData.products.length} SP
                    </span>
                  )}
                </button>
                {canManageProducts && <button
                  type="button"
                  onClick={() => setQuickProduct({ name: "" })}
                  className="flex size-11 desktop:size-9 shrink-0 cursor-pointer items-center justify-center rounded-md border border-rule bg-paper text-accent hover:border-accent/50 hover:bg-accent/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30"
                  aria-label="Thêm sản phẩm"
                  title="Thêm sản phẩm"
                >
                  <Plus className="size-4" />
                </button>}
              </div>

              {itemFields.length > 0 && (
                <ul className="divide-y divide-rule/60">
                  {itemFields.map((field, idx) => {
                    const it = watchItems[idx] || field
                    const linked = findProduct(it.productId)
                    const stock = typeof linked?.stock === "number" ? linked.stock : null
                    const qty = Number(it.qty) || 1
                    const lineTotal = qty * (Number(it.price) || 0)
                    const setQty = (n: number) => {
                      const cap = stock ?? Number.MAX_SAFE_INTEGER
                      if (n > cap) toast.info(`Kho chỉ còn ${cap} "${it.name}"`)
                      updateItem(idx, { ...it, qty: Math.min(cap, Math.max(1, Math.floor(n) || 1)) })
                    }
                    return (
                      <li key={field.id} className="flex flex-wrap items-center gap-x-2 gap-y-1.5 py-1.5">
                        <div className="flex min-w-0 flex-1 basis-40 items-center gap-2">
                          {linked?.imageUrl ? (
                            <img
                              src={linked.imageUrl}
                              alt=""
                              className="size-8 shrink-0 rounded-md object-cover ring-1 ring-rule"
                              onError={(e) => ((e.target as HTMLElement).style.display = "none")}
                            />
                          ) : (
                            <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-accent/10 text-accent">
                              <Package className="size-3.5" />
                            </span>
                          )}
                          <div className="min-w-0">
                            <p className="truncate text-sm text-ink" title={it.name}>{it.name}</p>
                            {stock != null && (
                              <p className={cn("text-xs", qty >= stock ? "font-medium text-warn" : "text-muted")}>còn {stock}</p>
                            )}
                          </div>
                          {linked && canManageProducts && (
                            <button
                              type="button"
                              onClick={() => setEditing({ product: linked, open: true })}
                              className="ml-auto inline-flex size-9 shrink-0 items-center justify-center rounded text-muted hover:bg-accent/10 hover:text-accent desktop:hidden cursor-pointer"
                              aria-label={`Sửa ${it.name}`}
                            >
                              <Pencil className="size-3.5" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => removeItem(idx)}
                            className={cn("inline-flex size-9 shrink-0 items-center justify-center rounded text-muted hover:bg-danger/10 hover:text-danger desktop:hidden cursor-pointer", !linked && "ml-auto")}
                            aria-label={`Bỏ ${it.name} khỏi đơn`}
                          >
                            <Trash2 className="size-3.5" />
                          </button>
                        </div>
                        <div className="flex w-full items-center gap-1.5 desktop:w-auto">
                          <div className="w-28">
                            <MoneyInput
                              value={it.price ?? 0}
                              onValueChange={(v) => updateItem(idx, { ...it, price: Math.max(0, v) })}
                              aria-label={`Đơn giá ${it.name}`}
                              className="h-9 desktop:h-8 bg-paper"
                            />
                          </div>
                          <div className="flex h-9 desktop:h-8 items-center rounded-md border border-rule bg-paper">
                            <button type="button" onClick={() => setQty(qty - 1)} disabled={qty <= 1} aria-label="Giảm số lượng" className="h-full px-2.5 text-inkSoft hover:text-ink disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed">−</button>
                            <input
                              type="number"
                              min={1}
                              max={stock ?? undefined}
                              value={it.qty ?? 1}
                              onChange={(e) => setQty(Number(e.target.value))}
                              aria-label={`Số lượng ${it.name}`}
                              className="w-8 bg-transparent text-center font-mono text-xs focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
                            />
                            <button type="button" onClick={() => setQty(qty + 1)} aria-label="Tăng số lượng" className="h-full px-2.5 text-inkSoft hover:text-ink cursor-pointer">+</button>
                          </div>
                          {/* Thành tiền chỉ khi > 1 cái (1 cái = đơn giá) — dòng vừa nửa cột trên máy tính */}
                          <span className="ml-auto shrink-0 font-mono text-xs font-medium text-ink">{qty > 1 ? money(lineTotal) : ""}</span>
                          {linked && canManageProducts && (
                            <button
                              type="button"
                              onClick={() => setEditing({ product: linked, open: true })}
                              className="hidden desktop:inline-flex size-7 shrink-0 items-center justify-center rounded text-muted hover:bg-accent/10 hover:text-accent cursor-pointer"
                              title="Sửa sản phẩm"
                              aria-label={`Sửa ${it.name}`}
                            >
                              <Pencil className="size-3.5" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => removeItem(idx)}
                            className="hidden desktop:inline-flex size-7 shrink-0 items-center justify-center rounded text-muted hover:bg-danger/10 hover:text-danger cursor-pointer"
                            title="Bỏ khỏi đơn"
                            aria-label={`Bỏ ${it.name} khỏi đơn`}
                          >
                            <Trash2 className="size-3.5" />
                          </button>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              )}

              {/* Kiện hàng gửi hãng: cân + kích thước người bán khai (nhiều món đóng chung một thùng) */}
              {watchCreateShipment && itemFields.length > 0 && (
                <div className="space-y-1.5 border-t border-rule/60 pt-2">
                  <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-2.5">
                    <div className="space-y-1">
                      <label htmlFor="parcel-weight" className="block text-xs font-medium text-ink">Cân nặng (kg)</label>
                      <Input
                        id="parcel-weight"
                        type="text"
                        inputMode="decimal"
                        value={commaDecimal(parcelWeightText ?? String(autoParcelGram / 1000))}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => setParcelWeightText(sanitizeDecimalInput(e.target.value, 3))}
                        onBlur={() => {
                          // Trống / sai / đúng bằng tổng cân sản phẩm → chạy theo sản phẩm lại
                          if (!declaredParcelGram || Math.round(declaredParcelGram * 1000) === autoParcelGram) setParcelWeightText(null)
                        }}
                        aria-invalid={parcelTooHeavy}
                        className={cn("bg-paper h-11 desktop:h-9 font-mono text-sm", parcelTooHeavy && "border-danger")}
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="block text-xs font-medium text-ink">Kích thước (cm)</span>
                      <div className="grid grid-cols-3 gap-1.5">
                        {(["Dài", "Rộng", "Cao"] as const).map((label, i) => (
                          <Input
                            key={label}
                            type="text"
                            inputMode="decimal"
                            aria-label={`${label} (cm)`}
                            placeholder={label}
                            value={commaDecimal(parcelDimText[i] ?? "")}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => {
                              const v = sanitizeDecimalInput(e.target.value, 1)
                              setParcelDimText((d) => d.map((x, j) => (j === i ? v : x)) as [string, string, string])
                            }}
                            aria-invalid={parcelDimsPartial && !parcelDimText[i]}
                            className={cn("bg-paper h-11 desktop:h-9 px-1.5 text-center font-mono text-sm", parcelDimsPartial && !parcelDimText[i] && "border-warn")}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  {parcelTooHeavy ? (
                    <p className="text-xs text-danger">Tối đa {kgText(MAX_PARCEL_GRAM)} kg</p>
                  ) : parcelDimsPartial ? (
                    <p className="text-xs text-warn">Nhập đủ dài, rộng, cao</p>
                  ) : (
                    // Quy tắc hãng: cân thực ≤ 3kg và quy đổi ≤ 5kg vẫn tính cân thực
                    parcel.chargeableGram > parcel.weightGram && (
                      <p className="text-xs text-warn">Tính cước theo cân quy đổi {kgText(parcel.volumetricGram)} kg</p>
                    )
                  )}
                </div>
              )}
              {codTouched && totalItemsSubtotal !== watchCod && <p className="text-xs text-warn">Tiền hàng ở mục Thanh toán đã sửa tay</p>}
            </div>
          </div>

          {/* Card 1: Người nhận — Khách quen (chọn trong danh bạ) / Khách lạ (nhập tay) */}
          <div className="rounded-xl bg-raise p-3 sm:p-3.5 space-y-2.5 shadow-card">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-semibold text-sm text-ink">Người nhận</h2>
              {picked && customerMode === "regular" && (
                <button
                  type="button"
                  onClick={() => setPicked(null)}
                  className="inline-flex min-h-11 desktop:min-h-0 cursor-pointer items-center text-xs text-accent hover:underline"
                >
                  Đổi khách
                </button>
              )}
              <div role="tablist" aria-label="Loại khách" className="ml-auto inline-flex rounded-lg bg-paper p-0.5">
                {([
                  ["regular", "Khách quen"],
                  ["new", "Khách lạ"],
                ] as const).map(([mode, label]) => (
                  <button
                    key={mode}
                    type="button"
                    role="tab"
                    aria-selected={customerMode === mode}
                    onClick={() => switchCustomerMode(mode)}
                    className={cn(
                      "h-10 desktop:h-8 cursor-pointer rounded-md px-3 text-xs font-medium transition-colors",
                      customerMode === mode ? "bg-raise text-ink shadow-sm" : "text-inkSoft hover:text-ink",
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {customerMode === "regular" && !picked ? (
              <CustomerContactBook onPick={pickFromBook} picking={pickingId} onUseNew={() => switchCustomerMode("new")} />
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2.5">
                  <Field
                    label={
                      <span className="inline-flex items-center gap-1.5">
                        Họ tên *
                        {picked && picked.boomCount > 0 && (
                          <span className="rounded-full bg-danger/10 px-1.5 py-0.5 text-xs font-medium text-danger">
                            Từng boom {picked.boomCount} đơn
                          </span>
                        )}
                      </span>
                    }
                    error={form.formState.errors.customer?.name?.message}
                  >
                    <Controller
                      control={form.control}
                      name="customer.name"
                      render={({ field }) => (
                        <Input id="recipient-name" {...NO_AUTOFILL} name="search-recipient-name" value={field.value ?? ""} onChange={field.onChange} onBlur={field.onBlur} className="bg-paper h-11 desktop:h-9" />
                      )}
                    />
                  </Field>

                  <Field
                    label={
                      <>
                        Điện thoại {watchCreateShipment && <span className="text-danger">*</span>}
                      </>
                    }
                  >
                    {/* Lỗi SĐT do chính PhoneInput hiện (kèm viền đỏ) — Field không hiện lần nữa */}
                    <Controller
                      control={form.control}
                      name="customer.phone"
                      render={({ field }) => (
                        <PhoneInput
                          id="recipient-phone"
                          value={field.value ?? null}
                          onValueChange={(v) => field.onChange(v ?? undefined)}
                          error={form.formState.errors.customer?.phone?.message}
                          hideIcon
                          placeholder=""
                          {...NO_AUTOFILL}
                          name="search-recipient-phone"
                          className="h-11 desktop:h-9 bg-paper"
                        />
                      )}
                    />
                  </Field>
                </div>

                {/* Khách lạ gõ trúng SĐT của khách quen → hỏi trước khi điền đè */}
                {!picked && exactMatch && (
                  <div className="flex flex-wrap items-center gap-2 rounded-lg bg-accent/5 px-3 py-2 text-xs">
                    <span className="text-ink">
                      SĐT này là khách quen <b>{exactMatch.name}</b>
                      {exactMatch.orderCount > 0 && ` · ${exactMatch.orderCount} đơn`}
                    </span>
                    <span className="ml-auto flex gap-1.5">
                      <Button type="button" size="sm" variant="ghost" className="h-10 desktop:h-7 text-xs" onClick={() => setDismissedPhone(phoneDigits)}>
                        Bỏ qua
                      </Button>
                      <Button type="button" size="sm" className="h-10 desktop:h-7 text-xs" onClick={() => applyCustomer(exactMatch)}>
                        Dùng thông tin đã lưu
                      </Button>
                    </span>
                  </div>
                )}

                {/* Địa chỉ đã lưu của khách quen — chọn một, hoặc "Địa chỉ khác" để nhập mới (tự lưu khi tạo đơn) */}
                {picked && picked.addresses.length > 0 && (
                  <div className="flex gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:none]" role="radiogroup" aria-label="Địa chỉ đã lưu">
                    {picked.addresses.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        role="radio"
                        aria-checked={pickedAddressId === a.id}
                        onClick={() => void applyAddress(a)}
                        title={addressLine(a)}
                        className={cn(
                          "inline-flex min-h-11 desktop:min-h-0 max-w-[16rem] shrink-0 cursor-pointer items-center gap-1 rounded-full border px-2.5 py-1 text-xs",
                          pickedAddressId === a.id ? "border-accent bg-accent/10 text-accent" : "border-rule bg-paper text-inkSoft hover:text-ink",
                        )}
                      >
                        <MapPin className="size-3 shrink-0" />
                        <span className="truncate">{a.label ? `${a.label} · ` : ""}{a.street}{a.ward ? `, ${a.ward}` : ""}</span>
                      </button>
                    ))}
                    <button
                      type="button"
                      role="radio"
                      aria-checked={pickedAddressId === null}
                      onClick={clearAddress}
                      className={cn(
                        "inline-flex min-h-11 desktop:min-h-0 shrink-0 cursor-pointer items-center rounded-full border px-2.5 py-1 text-xs",
                        pickedAddressId === null ? "border-accent bg-accent/10 text-accent" : "border-dashed border-rule text-inkSoft hover:text-ink",
                      )}
                    >
                      + Địa chỉ khác
                    </button>
                  </div>
                )}

                {/* Địa chỉ (trái) + bản đồ nhỏ (phải) */}
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_15rem]">
                  <AddressInput
                    idPrefix="recipient"
                    onIssueChange={setAreaIssue}
                    resolveNew
                    coords={orderLat != null && orderLng != null ? { lat: orderLat, lng: orderLng } : null}
                    value={{
                      street: watchCustomerAddress ?? "",
                      city: watchDestCity ?? "",
                      district: watchDestDistrict || null,
                      ward: watchDestWard ?? "",
                    }}
                    onChange={(v) => {
                      // Chỉ báo lỗi ngay khi đã bấm tạo đơn một lần — gõ dở không hiện lỗi
                      const opts = { shouldDirty: true, shouldValidate: form.formState.isSubmitted }
                      form.setValue("customer.address", v.street, opts)
                      form.setValue("shipment.destCity", v.city, opts)
                      form.setValue("shipment.destDistrict", v.district ?? "", opts)
                      form.setValue("shipment.destWard", v.ward, opts)
                    }}
                    streetError={form.formState.errors.customer?.address?.message}
                    areaError={form.formState.errors.shipment?.destCity?.message ?? form.formState.errors.shipment?.destWard?.message}
                  />

                  {isMobile ? (
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <Button type="button" variant="outline" size="sm" onClick={() => setCustomerMapOpen(true)} className="gap-1.5">
                        <MapPin className="size-4 text-accent" /> {orderLat != null ? "Đổi vị trí ghim" : "Ghim trên bản đồ"}
                      </Button>
                      {orderLat != null && (
                        <>
                          <span className="text-ok">Đã ghim vị trí giao</span>
                          <button
                            type="button"
                            onClick={() => {
                              setOrderLat(null)
                              setOrderLng(null)
                              setOrderGeoAddress(null)
                            }}
                            className="min-h-11 cursor-pointer text-muted underline"
                          >
                            Bỏ ghim
                          </button>
                        </>
                      )}
                    </div>
                  ) : (
                    <AddressMiniMap
                      className="h-32 lg:h-auto lg:min-h-[12rem]"
                      pin={orderLat != null && orderLng != null ? { lat: orderLat, lng: orderLng } : null}
                      address={{ street: watchCustomerAddress, ward: watchDestWard, district: watchDestDistrict ?? undefined, city: watchDestCity }}
                      onOpenPicker={() => setCustomerMapOpen(true)}
                      onClearPin={() => {
                        setOrderLat(null)
                        setOrderLng(null)
                        setOrderGeoAddress(null)
                      }}
                    />
                  )}
                </div>
              </>
            )}
          </div>

          {/* Card 3: Vận chuyển — hiện chỉ một hãng nên không cho chọn hãng, cũng không ghi tên hãng */}
          <div className="rounded-xl bg-raise p-3.5 sm:p-4 space-y-2.5 shadow-card">
            <div className="flex items-center gap-2">
              <h2 className="font-semibold text-sm text-ink">Vận chuyển</h2>
              <label className="ml-auto flex min-h-11 desktop:min-h-0 items-center gap-1.5 cursor-pointer select-none text-xs text-ink font-medium">
                <input type="checkbox" {...form.register("createShipment")} className="size-4 accent-accent rounded" />
                <span>Tạo vận đơn</span>
              </label>
            </div>

            {watchCreateShipment && (
              <>
                <Field label="Ghi chú cho bưu tá">
                  <Input
                    {...form.register("shipment.note")}
                    {...NO_AUTOFILL}
                    className="bg-paper h-11 desktop:h-9 text-xs sm:text-sm"
                  />
                </Field>
                {/* Ghi chú nhanh: một dòng, vuốt ngang trên điện thoại */}
                <div className="flex gap-1 overflow-x-auto pb-0.5 [scrollbar-width:none]">
                  {QUICK_NOTES.map((noteText) => (
                    <button
                      key={noteText}
                      type="button"
                      onClick={() => {
                        const current = form.getValues("shipment.note") || ""
                        if (!current) form.setValue("shipment.note", noteText)
                        else if (!current.includes(noteText)) form.setValue("shipment.note", `${current}, ${noteText}`)
                      }}
                      className="inline-flex min-h-11 desktop:min-h-0 shrink-0 items-center rounded-full border border-rule bg-paper px-2.5 py-0.5 text-xs text-inkSoft hover:text-ink transition-colors cursor-pointer"
                    >
                      + {noteText}
                    </button>
                  ))}
                </div>
                <div className="space-y-1.5">
                  <label className="flex w-fit min-h-11 desktop:min-h-0 items-center gap-1.5 cursor-pointer select-none text-xs text-ink font-medium">
                    <input
                      type="checkbox"
                      checked={scheduleOn}
                      onChange={(e) => {
                        const on = e.target.checked
                        setScheduleOn(on)
                        if (on && !sendAtLocal) setSendAtLocal(toLocalInput(defaultSendAt()))
                      }}
                      className="size-4 accent-accent rounded"
                    />
                    <span>Hẹn gửi</span>
                  </label>
                  {scheduleOn && (
                    <>
                      <Input
                        type="datetime-local"
                        aria-label="Hẹn gửi"
                        value={sendAtLocal}
                        min={toLocalInput(new Date(Date.now() + SEND_AT_MIN_LEAD_MS))}
                        onChange={(e) => setSendAtLocal(e.target.value)}
                        aria-invalid={Boolean(sendAtProblem)}
                        className={cn("bg-paper h-11 desktop:h-9 text-sm w-full sm:w-64", sendAtProblem && "border-danger")}
                      />
                      {sendAtProblem && <p className="text-xs text-danger">{sendAtProblem}</p>}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* ─── CỘT PHẢI: TỔNG KẾT & THANH TOÁN (4 cols) ─── */}
        <div className="xl:col-span-4 space-y-3 xl:sticky xl:top-20">
          {/* Card Cước & Thanh toán (kèm mã tham chiếu của shop ở đầu thẻ) */}
          <div className="rounded-xl bg-raise p-3.5 sm:p-4 space-y-3 shadow-card">
            <div className="hidden desktop:block space-y-1">
              <div className="flex items-center gap-2">
                <Label htmlFor="clientOrderId" className="shrink-0 text-xs font-medium text-inkSoft">Mã tham chiếu</Label>
                <Input
                  id="clientOrderId"
                  {...form.register("clientOrderId")}
                  aria-invalid={Boolean(form.formState.errors.clientOrderId)}
                  className={cn(
                    "font-mono text-xs font-medium h-10 desktop:h-8 bg-paper flex-1 min-w-0",
                    form.formState.errors.clientOrderId && "border-danger focus-visible:ring-danger/20",
                  )}
                />
                <button
                  type="button"
                  onClick={() => {
                    form.setValue("clientOrderId", makeClientOrderId("DH"), { shouldValidate: true })
                    form.clearErrors("clientOrderId")
                  }}
                  className="inline-flex min-h-11 desktop:min-h-0 shrink-0 cursor-pointer items-center text-xs text-accent hover:underline"
                >
                  Đổi mã
                </button>
              </div>
              {form.formState.errors.clientOrderId?.message && <p className="text-xs text-danger">{form.formState.errors.clientOrderId.message}</p>}
            </div>
            <div className="flex items-center justify-between pb-2 border-b border-rule">
              <div className="flex items-center gap-1.5 font-semibold text-xs text-ink">
                <span>Thanh toán</span>
              </div>
              <div className="flex items-center gap-1.5">
                {currentRoute ? (
                  <span
                    className={`px-2 py-0.5 rounded-full border text-xs font-semibold flex items-center gap-1.5 shadow-2xs ${ROUTE_BADGES[currentRoute].bgClass} ${ROUTE_BADGES[currentRoute].textClass}`}
                  >
                    <span className="size-1.5 rounded-full bg-current shrink-0" />
                    <span>{ROUTE_BADGES[currentRoute].label}</span>
                  </span>
                ) : (
                  <span className="rounded-full bg-warn/10 px-2 py-0.5 text-xs font-semibold text-warn">
                    Chưa rõ vùng
                  </span>
                )}
                {quote && (
                  <span className="px-2 py-0.5 rounded bg-accent/10 text-accent font-semibold text-xs">
                    {quote.zoneLabel} · {(quote.chargeableWeightGram / 1000).toLocaleString("vi-VN")}kg
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <Field label="Tiền hàng">
                <Controller
                  control={form.control}
                  name="cod"
                  render={({ field }) => (
                    <MoneyInput
                      value={field.value}
                      onValueChange={(n) => {
                        if (n === field.value) return // rời ô mà không sửa
                        field.onChange(n)
                        // Sửa tay → thôi chạy theo tổng tiền hàng; sửa về đúng tổng thì chạy theo lại
                        setCodTouched(n !== totalItemsSubtotal)
                      }}
                      placeholder="0"
                      className="h-11 desktop:h-9 bg-paper"
                    />
                  )}
                />
                {codTouched && watchCod !== totalItemsSubtotal && (
                  <p className="flex flex-wrap items-center gap-x-2 text-xs text-warn">
                    <span>Đã sửa tay — khác tổng tiền hàng {money(totalItemsSubtotal)}.</span>
                    <button type="button" onClick={resetCodToSubtotal} className="font-medium text-accent hover:underline cursor-pointer">
                      Đặt lại theo tiền hàng
                    </button>
                  </p>
                )}
              </Field>
            </div>

            {/* Người trả cước — chỉ có nghĩa khi tạo vận đơn */}
            {watchCreateShipment && (
              <div className="space-y-1.5 pt-1">
                <span className="text-xs font-medium text-ink">Người trả cước</span>
                <div className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-paper border border-rule" role="radiogroup" aria-label="Người trả cước">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={watchPayer === 0}
                    disabled={!canShipPrepay}
                    onClick={() => {
                      form.setValue("shipment.shipmentPayer", 0)
                      setFeeTouched(false)
                      setCustomerShippingFee(0)
                    }}
                    className={cn(
                      "py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer text-center disabled:cursor-not-allowed disabled:opacity-50",
                      watchPayer === 0
                        ? "bg-accent text-onAccent  font-semibold"
                        : "text-inkSoft hover:text-ink",
                    )}
                  >
                    Shop trả
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={watchPayer === 1}
                    onClick={() => {
                      form.setValue("shipment.shipmentPayer", 1)
                      setFeeTouched(false)
                    }}
                    className={cn(
                      "py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer text-center",
                      watchPayer === 1
                        ? "bg-accent text-onAccent  font-semibold"
                        : "text-inkSoft hover:text-ink",
                    )}
                  >
                    Khách trả
                  </button>
                </div>
              </div>
            )}

            {customerPaysShipping && (
              <Field label="Phí ship thu khách">
                <MoneyInput
                  value={customerShippingFee}
                  onValueChange={(v) => {
                    const n = Number(v) || 0
                    if (n === customerShippingFee) return // rời ô mà không sửa
                    setFeeTouched(true)
                    setCustomerShippingFee(n)
                  }}
                  placeholder="0"
                  className="h-11 desktop:h-9 bg-paper"
                />
              </Field>
            )}

            {/* Bảng tổng kết dòng tiền — §2.6.2 */}
            <div className="p-3 rounded-lg bg-paper border border-rule space-y-1.5 text-xs">
              {customerPaysShipping ? (
                <div className="flex items-center justify-between text-inkSoft">
                  <span>Tiền hàng + phí ship thu khách:</span>
                  <span className="font-mono">{money(goodsValue)} + {money(shipFee)}</span>
                </div>
              ) : (
                <div className="flex items-center justify-between text-inkSoft">
                  <span>Tiền hàng:</span>
                  <span className="font-mono">{money(goodsValue)}</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-1.5 border-t border-rule font-semibold text-sm text-ink">
                <span>COD:</span>
                <span className="font-mono text-accent text-base">{money(codTotal)}</span>
              </div>
              {watchCreateShipment && (
                <>
                  {shopPaysShipping ? (
                    <div className="flex items-center justify-between text-inkSoft">
                      <span>Tiền ship chuyển khoản trước:</span>
                      <span className="font-mono">{quote ? money(prepayEstimate) : quoting ? "…" : "—"}</span>
                    </div>
                  ) : seesRates ? (
                  <div className="flex items-center justify-between text-inkSoft">
                    <span>Cước theo bảng giá của bạn:</span>
                    <span className="font-mono">{quote ? `− ${money(sellerPrice)}` : quoting ? "…" : "—"}</span>
                  </div>
                  ) : null}
                  {/* Thực nhận / công nợ / phí hoàn: chỉ người được xem tiền (nhân viên không có quyền thì ẩn) */}
                  {seesMoney && (
                  <div className="flex items-center justify-between pt-0.5 text-sm">
                    <span className="text-muted">{shopPaysShipping ? "Thực nhận khi giao:" : "Thực nhận dự kiến:"}</span>
                    <span className={cn("font-mono font-semibold", netEstimate < 0 ? "text-danger" : "text-ok")}>{money(netEstimate)}</span>
                  </div>
                  )}
                  {seesMoney && quote && netEstimate < 0 && !shopPaysShipping && (
                    <p className="text-danger">
                      Đơn này tạo công nợ {money(-netEstimate)} — sẽ trừ vào tiền COD các đơn sau.
                    </p>
                  )}
                  {seesMoney && quote && (
                    <p className="hidden desktop:block text-muted">
                      Nếu hoàn hàng: bị trừ {money(sellerPrice + quote.returnFeeEstimate)} (cước + phí hoàn {quote.returnFeePercent}%).
                    </p>
                  )}
                  {quoteError && <p className="text-danger">{(quoteError as Error).message}</p>}
                </>
              )}
            </div>

            {/* Nút hành động */}
            <div className="space-y-2 pt-1">
              {shipmentBlocked && (
                <div role="alert" className="p-2.5 rounded-lg border border-danger/30 bg-danger/10 text-danger text-xs">
                  {shipmentBlocked.message}
                </div>
              )}
              <Button
                type="submit"
                size="lg"
                disabled={submitDisabled}
                // Điện thoại dùng thanh dính đáy (MobileActionBar) — không lặp nút ở cuối trang
                className="hidden desktop:inline-flex w-full font-semibold h-11 text-sm bg-accent hover:bg-accent/90 text-onAccent cursor-pointer shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="size-4 animate-spin mr-2" />
                    Đang tạo đơn...
                  </>
                ) : (
                  <>
                    {submitLabel}
                  </>
                )}
              </Button>

              {onCancel ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => requestLeave(onCancel)}
                  className="w-full text-xs text-muted hover:text-ink h-11 desktop:h-9 cursor-pointer"
                >
                  Hủy
                </Button>
              ) : (
                <Link to="/orders" className="block text-center text-xs text-muted hover:text-ink pt-1">
                  Hủy
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Điện thoại: COD + nút tạo đơn luôn trong tầm tay */}
      <MobileActionBar>
        <div className="min-w-0 flex-1">
          <p className="text-xs text-muted">COD</p>
          <p className="truncate font-mono text-lg font-bold tabular-nums text-accent">{money(codTotal)}</p>
          {shipmentBlocked && <p className="truncate text-xs text-danger">{shipmentBlocked.message}</p>}
        </div>
        <Button type="submit" disabled={submitDisabled} className="h-11 shrink-0 px-5 font-semibold">
          {submitting ? <Loader2 className="size-4 animate-spin" /> : submitLabel}
        </Button>
      </MobileActionBar>
    </form>

    {/* Ngoài <form> (và tự dựng qua portal): Enter trong ô tìm / số nhà không gửi đơn */}
    <AddressMapPickerModal
      open={customerMapOpen}
      onClose={() => setCustomerMapOpen(false)}
      onConfirm={handleCustomerMapConfirm}
      initialLat={orderLat ?? undefined}
      initialLng={orderLng ?? undefined}
      initialAddressText={[watchCustomerAddress, watchDestWard, watchDestDistrict, watchDestCity].filter(Boolean).join(", ")}
    />
    <ProductPickerDialog
      open={pickerOpen}
      onOpenChange={setPickerOpen}
      products={productData?.products}
      inCart={inCart}
      cartQty={totalItemsQty}
      cartTotal={totalItemsSubtotal}
      onSetQty={setProductQty}
      onCreateNew={canManageProducts ? (name) => setQuickProduct({ name }) : undefined}
    />
    <QuickProductDialog
      open={quickProduct !== null}
      onOpenChange={(o) => !o && setQuickProduct(null)}
      initialName={quickProduct?.name}
      onCreated={(p) => {
        setProductQty(p, 1)
        toast.success(`Đã thêm "${p.name}" vào kho và vào đơn`)
      }}
    />
    <QuickProductDialog
      open={editing?.open ?? false}
      onOpenChange={(o) => !o && setEditing((e) => e && { ...e, open: false })}
      product={editing?.product ?? null}
      onUpdated={(p) => {
        if (editing) applyProductEdit(editing.product, p)
        toast.success(`Đã sửa "${p.name}" trong kho và trong đơn`)
      }}
    />
    <SaveCustomerOnLeaveDialog
      open={leaving?.open ?? false}
      customer={leaving?.customer ?? null}
      onStay={() => setLeaving((l) => l && { ...l, open: false })}
      onLeave={() => {
        const go = leaving?.go
        setLeaving(null)
        go?.()
      }}
    />
    </>
  )
}

function Field({
  label,
  hint,
  error,
  className,
  children,
}: {
  label: React.ReactNode
  hint?: string
  error?: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={cn("space-y-1.5", className)}>
      <Label className="text-xs font-medium text-ink">
        {label} {hint && <span className="text-muted font-normal text-xs">— {hint}</span>}
      </Label>
      {children}
      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  )
}
